import styled from '@emotion/styled';
import { Grid } from '@mui/material';
import { WalletIcon } from 'assets/Icons';
import { NewUseUpdatedCart } from 'components/molecules/Cart/hooks';
import QUERY_KEYS from 'network/config/queryKeys';
import { getWalletBalance } from 'network/services/wallet';
import { CheckoutProduct, PointModal } from 'pages/Checkout/component';
import { CheckoutItems, PriceSummary } from 'pages/Checkout/style';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { checkoutDetailsAtom } from 'store/atoms/checkoutAtom';
import checkoutVariablesAtom from 'store/atoms/checkoutVariables';
import { pointsAtom } from 'store/atoms/points';
import { userRoleAtom } from 'store/atoms/userAtom';
import { formatCurrency } from 'utils/currencyFormater';

const ProductsGrid = styled('div')`
  display: grid;
  gap: 16px;
  margin-bottom: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
`;

const ProductCard = styled('div')`
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  @media (max-width: 767px) {
    border: none;
    border-bottom: 1px solid #e8e8e8;
    border-radius: 0;
    padding: 12px 0;
    box-shadow: none;

    &:hover {
      box-shadow: none;
      transform: none;
    }

    &:last-child {
      border-bottom: none;
    }
  }
`;

const CheckoutSummary = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [points, setPoints] = useRecoilState(pointsAtom);
  const [user, checkNewUser] = useState('');
  const userRole = useRecoilValue(userRoleAtom);

  const checkoutDetails = useRecoilValue(checkoutDetailsAtom);
  const checkoutVariables = useRecoilValue(checkoutVariablesAtom);

  const serverCartQuery = NewUseUpdatedCart();
  const serverCart = serverCartQuery?.data;
  const cartOrder = (serverCart?.orders as any[])?.find(order => order?.locationId === Number(orderId));
  const cartReady = useRef(false);
  const [cartChecked, setCartChecked] = useState(false);

  // Fetch wallet balance
  const { data: walletData } = useQuery({
    queryFn: getWalletBalance,
    queryKey: [QUERY_KEYS.WALLET_BALANCE],
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const walletBalance = walletData?.data?.data?.balance || 0;

  const subTotal = cartOrder?.totalAmount || 0;

  useEffect(() => {
    if (cartReady.current) return;
    cartReady.current = true;
    serverCartQuery?.refetch().finally(() => setCartChecked(true));
  }, [serverCartQuery]);

  useEffect(() => {
    if (!cartChecked) return;
    if (!cartOrder) {
      navigate('/account/orders');
    }
  }, [cartChecked, cartOrder, navigate]);

  useEffect(() => {
    return checkNewUser(localStorage.getItem('newuser') ?? 'false');
  }, []);

  const productCount = cartOrder?.products?.length || 0;

  return (
    <>
      <Grid item xs={12} md={4.5} lg={3.75}>
        <CheckoutItems>
          {/* <CheckoutHeader>
            <h3>Order Items</h3>
            <span className="product-count">
              {productCount} {productCount === 1 ? 'item' : 'items'}
            </span>
          </CheckoutHeader> */}

          <ProductsGrid>
            {cartOrder?.products?.map((item: any, index: number) => (
              <ProductCard key={item.productId}>
                <CheckoutProduct item={{ ...item, index }} orderId={cartOrder?.id} customerType={userRole} />
              </ProductCard>
            ))}
          </ProductsGrid>
        </CheckoutItems>

        <PriceSummary>
          {user === 'true' && (
            <div
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '8px',
                marginBottom: '16px',
                padding: '12px 16px'
              }}>
              <p className="font-bold text-sm">🎉 10% Discount On Your First Transaction!</p>
            </div>
          )}

          <div className="subtotal">
            <p>Subtotal</p>
            <p>{formatCurrency(cartOrder?.totalAmount || 0)}</p>
          </div>

          <div className="delivery">
            <p>Delivery</p>
            <p>{formatCurrency(checkoutDetails.shippingFee ?? 0)}</p>
          </div>

          <div className="total">
            <p>Total</p>
            <p>{formatCurrency(subTotal + (checkoutDetails.shippingFee ?? 0))}</p>
          </div>

          <div className="wallet">
            <p className="wallet-point" onClick={() => setPoints(c => ({ ...c, open: true }))}>
              <WalletIcon /> <span className="wallet-text">Wallet Balance</span>
            </p>
            <p className="flex items-center gap-2">
              <span>{formatCurrency(walletBalance)}</span>
            </p>
          </div>

          <div className="grandtotal">
            <p>Grand Total</p>
            <p>
              {formatCurrency(
                subTotal +
                (checkoutDetails.shippingFee ?? 0) -
                (isNaN(checkoutVariables.pointsUsed.pointNairaEquivalent)
                  ? 0
                  : checkoutVariables.pointsUsed.pointNairaEquivalent)
              )}
            </p>
          </div>
        </PriceSummary>
      </Grid>
      {points.open && <PointModal />}
    </>
  );
};

export default CheckoutSummary;
